/**
 * WORKSPACE FEATURE GATING
 *
 * Fail-closed design: all features default false, explicitly true per workspace type.
 * Used in both UI components (hide buttons) and store actions (block mutations).
 *
 * Local workspaces can start/stop/retry tasks when the local worker is running
 * and all dependencies are satisfied.
 */

import { useWorkspaceStore, getWorkspaceType, type Workspace } from "@/stores/workspaceStore";
import { isTauri } from "@/hooks/usePlatform";
import { useWorkerStore } from "@/stores/workerStore";

export interface WorkspaceFeatures {
  // Mutation gates (fail-closed: default false)
  canStartTask: boolean;
  canStopTask: boolean;
  canRetryPipeline: boolean;
  canMergePR: boolean;
  canSyncIssues: boolean;

  // Feature visibility gates
  canPreview: boolean;
  canTerminal: boolean;
  canCloudResources: boolean;
  canSecrets: boolean;
  canGit: boolean;
  canAiChat: boolean;
  canBilling: boolean;

  // Workspace gates
  canCreateLocalWorkspace: boolean;
  canManageTeam: boolean;
  isLocalWorkspace: boolean;
  isCloudWorkspace: boolean;
}

export function useWorkspaceFeatures(): WorkspaceFeatures {
  const workspace = useWorkspaceStore((s) => s.getActiveWorkspace()) as Workspace | undefined;
  const wsType = getWorkspaceType(workspace);
  const isLocal = wsType === "local";
  const isCloud = wsType === "cloud";

  // Keep task gating aligned with the worker store's own readiness logic.
  // This avoids frontend/backend shape mismatches in deps checks.
  const workerAvailable = useWorkerStore((s) => s.isWorkerAvailable());

  return {
    // Task mutations: cloud always, local when worker is available
    canStartTask: isCloud || (isLocal && workerAvailable),
    canStopTask: isCloud || (isLocal && workerAvailable),
    canRetryPipeline: isCloud || (isLocal && workerAvailable),

    // PR merge: cloud always, local when in Tauri (has PAT in keychain)
    canMergePR: isCloud || (isLocal && isTauri()),
    canSyncIssues: isCloud,

    // Feature visibility
    canPreview: isCloud || (isLocal && isTauri()),
    canTerminal: isCloud || (isLocal && isTauri()),
    canCloudResources: isCloud,
    canSecrets: isLocal && isTauri(),
    canGit: isCloud || (isLocal && isTauri()),
    canAiChat: isLocal && isTauri(),
    canBilling: isCloud || (isLocal && !!workspace?.linkedWorkspaceId),

    // Local workspace creation only in Tauri
    canCreateLocalWorkspace: isTauri(),
    canManageTeam: isCloud,
    isLocalWorkspace: isLocal,
    isCloudWorkspace: isCloud,
  };
}
